import { Component, Input } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataSharedService } from 'src/app/services/data-shared.service';
import { SuccessModalComponent } from '../success-modal/success-modal.component';
import { SuccessModal } from 'src/app/app.model';
import { IMPORT_FIELD_ENDPOINTS } from 'src/app/app.constant';
import { normalizeImportFields } from 'src/app/utils/import-field-normalizer';

@Component({
  selector: 'app-column-mapping-import',
  templateUrl: './column-mapping-import.component.html',
  styleUrls: ['./column-mapping-import.component.css']
})
export class ColumnMappingImportComponent {
  @Input() systemFields: { key: string; label: string }[] = [];
  @Input() previewUrl: string;
  @Input() startUrl: string;
  @Input() importUrl: string = '/api/import/start';
  @Input() templateKey: string = 'defaultMappingTemplate';
  @Input() entity: string;

  currentStep: number = 1; // 1 = Upload, 2 = Map, 3 = Importing
  isOpen = false;
  selectedFile: File | null = null;
  headers: string[] = [];
  headerLabels: string[] = [];
  previewRows: any[] = [];
  columnMapping: { [header: string]: string | null } = {};
  loading = false;
  importStatus: string = 'submitted';
  mappingError: string | null = null;

  constructor(
    private activeModalService: NgbActiveModal,
    private dataSharedService: DataSharedService,
    private modalService: NgbModal,
  ) { }

  ngOnInit() {
    if (!this.systemFields.length) {
      this.loadSystemFields();
    }
  }

  loadSystemFields() {
    const endpoint = IMPORT_FIELD_ENDPOINTS[this.entity];

    if (!endpoint) {
      console.warn(`No field endpoint configured for entity: ${this.entity}`);
      return;
    }

    this.dataSharedService.getImportFields(endpoint).subscribe(res => {
      this.systemFields = normalizeImportFields(this.entity, res);
    });
  }

  open() {
    this.reset();
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
  }

  reset() {
    this.selectedFile = null as any;
    this.headers = [];
    this.headerLabels = [];
    this.previewRows = [];
    this.columnMapping = {};
    this.loading = false;
    this.currentStep = 1;
    this.importStatus = 'submitted';
    this.mappingError = null;
  }

  onFileChange(event: any) {
    if (event.target.files.length > 0) {
      this.selectedFile = event.target.files[0];
      this.previewFile();
    }
  }

  previewFile() {
    const formData = new FormData();

    if (!this.selectedFile) {
      return;
    }

    formData.append('file', this.selectedFile);

    this.loading = true;

    this.dataSharedService.importPreview(this.previewUrl, formData).subscribe({
      next: (res) => {
        this.headers = res.headers;
        this.previewRows = res.rows;
        this.headerLabels = res.headerLabels;
        this.loading = false;
        // Ensure system fields are loaded (handles case where entity was set after ngOnInit)
        if (!this.systemFields.length) {
          this.loadSystemFields();
        }
        // Auto-advance to step 2
        this.currentStep = 2;
      },
      error: () => (this.loading = false)
    });
  }

  startImport() {
    if (!this.selectedFile) return;

    this.mappingError = null;

    // Client-side validation: check required fields are mapped
    const mappedValues = Object.values(this.columnMapping).filter(v => v != null && v !== '');
    if (this.entity === 'Timesheets') {
      const requiredFields = [
        { key: 'contractor_email', label: 'Contractor Email' },
        { key: 'job', label: 'Job' },
        { key: 'start_date', label: 'Start Date' },
      ];
      console.log(mappedValues);
      const missing = requiredFields.filter(f => !mappedValues.includes(f.key));
      if (missing.length) {
        this.mappingError = `The following required columns must be mapped: ${missing.map(f => f.label).join(', ')}`;
        return;
      }
    }

    this.currentStep = 3;

    const formData = new FormData();
    formData.append('file', this.selectedFile);
    formData.append('mapping', JSON.stringify(this.columnMapping));
    if (this.entity === 'Timesheets') {
      formData.append('import_status', this.importStatus);
    }

    this.dataSharedService.importStart(this.startUrl, formData).subscribe({
      next: () => {
        this.successModal('Import process started! We will notify you once the process is completed.');
      },
      error: (err) => {
        this.currentStep = 2;
        this.mappingError = err?.error?.message || 'Error starting import. Please try again.';
      }
    });
  }

  successModal(message: string) {
    const modalRef = this.modalService.open(SuccessModalComponent, {
      size: 'sm',
      modalDialogClass: 'success-modal',
      backdrop: false,
    });

    let data: SuccessModal = {
      message: message,
      image: 'assets/img/checkmark.png',
      duration: 3500,
    }

    modalRef.componentInstance.data = data;
    modalRef.result.then(() => {
      this.activeModalService.close();
    });
  }

  onClose() {
    this.activeModalService.close();
  }
}
